import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { LockOutlined as LockOutlinedIcon } from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import { useApi } from 'lib/api';
import { puedeUsarDefinicionNomina } from 'lib/seguridad/permisosPorModulo';
import { ConfirmarDialog, FP, PageHeader, fpDataGridSx, fpInputSx } from 'components/financepro';

export { puedeUsarDefinicionNomina };

const btnBase = { textTransform: 'none', borderRadius: 1.8, fontWeight: 700, boxShadow: 'none' };
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: '#FFFFFF',
  border: `1px solid ${c}`,
  '&:hover': { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
  '&.Mui-disabled': { color: FP.color.textMuted, borderColor: FP.color.border },
});
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: '#FFFFFF',
  '&:hover': { bgcolor: hover, boxShadow: 'none', color: '#FFFFFF' },
  '&.Mui-disabled': { bgcolor: `${bg}66`, color: '#FFFFFF' },
});

const cardShellSx = {
  borderRadius: 2.5,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  border: `1px solid ${FP.color.border}`,
  overflow: 'hidden',
};

const formularioVacio = {
  fecha1: '',
  fecha2: '',
  fechapago: '',
  ano: '',
};

function formatoFecha(valor) {
  const texto = String(valor || '').trim();
  if (!texto || texto.length < 10) return '—';
  const [y, m, d] = texto.slice(0, 10).split('-');
  return d && m && y ? `${d}-${m}-${y}` : texto;
}

function etiquetaEstado(estado) {
  const valor = String(estado || '').trim().toUpperCase();
  if (valor === 'N') return 'Activa';
  if (valor === 'C') return 'Cerrada';
  return valor || '—';
}

function sumarDiasIso(iso, dias) {
  const [y, m, d] = String(iso || '').slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return '';
  const fecha = new Date(y, m - 1, d);
  fecha.setDate(fecha.getDate() + dias);
  const yy = fecha.getFullYear();
  const mm = String(fecha.getMonth() + 1).padStart(2, '0');
  const dd = String(fecha.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

function diasPorPeriodicidad(periodicidad) {
  if (periodicidad === 'Semanal') return 6;
  if (periodicidad === 'Mensual') return 30;
  return 15;
}

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: '100%', py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin períodos para este año
      </Typography>
    </Stack>
  );
}

/**
 * Definición de la tabla de nómina (fps1070). Crea el período en `tablames`.
 * El menú y Acciones solo la muestran si el rol tiene `definicionnomina`.
 */
const DefinicionNomina = ({ open = false, onClose, embedded = false }) => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { usuarioLog, roleDetalles } = controller;
  const { post } = useApi();

  const puedeUsar = useMemo(() => puedeUsarDefinicionNomina(roleDetalles), [roleDetalles]);
  const puedeOperar = Boolean(selectedCompany?.db_empresa && usuarioLog);

  const [cargando, setCargando] = useState(false);
  const [consultando, setConsultando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [periodicidad, setPeriodicidad] = useState('');
  const [ultimaNomina, setUltimaNomina] = useState('');
  const [anoProceso, setAnoProceso] = useState('');
  const [formulario, setFormulario] = useState(formularioVacio);
  const [periodos, setPeriodos] = useState([]);
  const [eliminando, setEliminando] = useState(null);
  const [borrando, setBorrando] = useState(false);

  const credencialesEmpresa = useCallback(
    () => ({ id_empresa: selectedCompany }),
    [selectedCompany]
  );

  const aplicarPayload = useCallback((data, actualizarFormulario) => {
    setPeriodicidad(data.periodicidad || '');
    setUltimaNomina(data.ultima_nomina || '');
    setPeriodos(Array.isArray(data.periodos) ? data.periodos : []);
    const ano = data.ano != null && data.ano !== '' ? String(data.ano) : '';
    setAnoProceso(ano);
    if (!actualizarFormulario) return;
    const sugerido = data.sugerido || {};
    setFormulario({
      fecha1: sugerido.fecha1 || '',
      fecha2: sugerido.fecha2 || '',
      fechapago: sugerido.fechapago || '',
      ano: sugerido.ano != null ? String(sugerido.ano) : ano,
    });
  }, []);

  const fetchDefinicion = useCallback(
    async (ano) => {
      const payload = { ...credencialesEmpresa() };
      if (ano) payload.ano = ano;
      const { ok, data } = await post('/api/nomina_obtenerdefinicion', payload);
      if (!ok) throw new Error(data.Error || 'Error al leer la tabla de nómina');
      return data;
    },
    [post, credencialesEmpresa]
  );

  const cargar = useCallback(async () => {
    if (!puedeUsar || !puedeOperar) {
      setPeriodos([]);
      return;
    }
    setCargando(true);
    try {
      aplicarPayload(await fetchDefinicion(), true);
    } catch (e) {
      setPeriodos([]);
      showError(e.message || 'Error al leer la tabla de nómina');
    } finally {
      setCargando(false);
    }
  }, [puedeUsar, puedeOperar, fetchDefinicion, aplicarPayload, showError]);

  useEffect(() => {
    if ((open || embedded) && puedeUsar && puedeOperar) cargar();
  }, [open, embedded, puedeUsar, puedeOperar, cargar]);

  const cerrar = () => {
    if (guardando || borrando) return;
    if (onClose) onClose();
  };

  const consultarAno = async () => {
    const ano = String(anoProceso || '').trim();
    if (!ano) {
      showError('El ano para el proceso esta en blanco ');
      return;
    }
    setConsultando(true);
    try {
      aplicarPayload(await fetchDefinicion(ano), false);
    } catch (e) {
      showError(e.message || 'Error al leer la tabla de nómina');
    } finally {
      setConsultando(false);
    }
  };

  const confirmarEliminar = async () => {
    if (!puedeUsar) {
      showError('No tiene permisos para definir la nómina');
      return;
    }
    if (!eliminando) return;
    if (eliminando.puede_eliminar === false) {
      showError('No se puede eliminar: existen pagos historicos para esta nomina');
      setEliminando(null);
      return;
    }
    setBorrando(true);
    try {
      const { ok, data } = await post('/api/nomina_eliminardefinicion', {
        ...credencialesEmpresa(),
        ano: eliminando.ano,
        numero: eliminando.numero,
      });
      if (!ok) throw new Error(data.Error || 'No se pudo eliminar la definición');
      showSuccess(data.ok || 'Definicion de nomina eliminada');
      aplicarPayload(data, true);
      setEliminando(null);
    } catch (e) {
      showError(e.message || 'No se pudo eliminar la definición');
    } finally {
      setBorrando(false);
    }
  };

  const anadir = async () => {
    if (!puedeUsar) {
      showError('No tiene permisos para definir la nómina');
      return;
    }
    if (!formulario.fecha1 || !formulario.fecha2 || !formulario.fechapago) {
      showError('Las fechas de la nomina no son validas');
      return;
    }
    setGuardando(true);
    try {
      const { ok, data } = await post('/api/nomina_definirperiodo', {
        ...credencialesEmpresa(),
        fecha1: formulario.fecha1,
        fecha2: formulario.fecha2,
        fechapago: formulario.fechapago,
        ano: formulario.ano,
      });
      if (!ok) throw new Error(data.Error || 'No se pueden grabar los datos');
      showSuccess(data.ok || 'Nomina definida');
      aplicarPayload(data, true);
    } catch (e) {
      showError(e.message || 'No se pueden grabar los datos');
    } finally {
      setGuardando(false);
    }
  };

  const setCampo = (campo) => (evento) => {
    const valor = evento?.target?.value ?? '';
    setFormulario((prev) => {
      const siguiente = { ...prev, [campo]: valor };
      if (campo === 'fecha1' && valor && valor.length >= 10) {
        siguiente.ano = valor.slice(0, 4);
        siguiente.fecha2 = sumarDiasIso(valor, diasPorPeriodicidad(periodicidad));
        siguiente.fechapago = sumarDiasIso(siguiente.fecha2, 1);
      }
      return siguiente;
    });
  };

  const columnas = useMemo(
    () => [
      { field: 'ano', headerName: 'Año', width: 80 },
      { field: 'numero', headerName: 'Nómina', width: 90 },
      {
        field: 'fecha1',
        headerName: 'F. Inicial',
        flex: 1,
        minWidth: 110,
        valueFormatter: (v) => formatoFecha(v),
      },
      {
        field: 'fecha2',
        headerName: 'F. Final',
        flex: 1,
        minWidth: 110,
        valueFormatter: (v) => formatoFecha(v),
      },
      {
        field: 'fechapago',
        headerName: 'F. Pago',
        flex: 1,
        minWidth: 110,
        valueFormatter: (v) => formatoFecha(v),
      },
      {
        field: 'estado',
        headerName: 'Estado',
        width: 110,
        renderCell: (params) => {
          const activo = String(params.value || '').toUpperCase() === 'N';
          return (
            <Chip
              size="small"
              label={etiquetaEstado(params.value)}
              sx={{
                fontWeight: 700,
                bgcolor: activo ? FP.color.accentBlueSoft : FP.color.surfaceAlt,
                color: activo ? FP.color.accentBlue : FP.color.textSecondary,
              }}
            />
          );
        },
      },
      {
        field: 'acciones',
        headerName: 'Acciones',
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Tooltip
            title={
              params.row.puede_eliminar === false
                ? 'Hay pagos históricos para este año y nómina'
                : 'Eliminar'
            }
          >
            <span>
              <IconButton
                size="small"
                onClick={() => setEliminando(params.row)}
                disabled={params.row.puede_eliminar !== true || borrando}
                sx={{
                  color: '#B3261E',
                  '&.Mui-disabled': { color: FP.color.textMuted },
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        ),
      },
    ],
    [borrando]
  );

  const cuerpo = !puedeOperar ? (
    <Typography sx={{ color: FP.color.textSecondary, py: 2 }}>
      Seleccione una empresa para definir la nómina.
    </Typography>
  ) : cargando ? (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
      <CircularProgress size={28} sx={{ color: FP.color.navy }} />
    </Box>
  ) : (
    <Grid container spacing={2} sx={{ mt: 0 }}>
      <Grid item xs={12} sm={6} md={3}>
        <TextField
          fullWidth
          type="date"
          label="Inicio"
          value={formulario.fecha1}
          onChange={setCampo('fecha1')}
          InputLabelProps={{ shrink: true }}
          sx={fpInputSx}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <TextField
          fullWidth
          type="date"
          label="Fin"
          value={formulario.fecha2}
          onChange={setCampo('fecha2')}
          InputLabelProps={{ shrink: true }}
          sx={fpInputSx}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <TextField
          fullWidth
          label="Año nómina"
          value={formulario.ano}
          onChange={(e) =>
            setFormulario((prev) => ({
              ...prev,
              ano: String(e.target.value || '').replace(/\D/g, '').slice(0, 4),
            }))
          }
          inputProps={{ maxLength: 4, inputMode: 'numeric' }}
          InputLabelProps={{ shrink: true }}
          sx={fpInputSx}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <TextField
          fullWidth
          label="Secuencia de pago"
          value={periodicidad}
          helperText="Tomada de Nómina inicial"
          InputProps={{ readOnly: true }}
          InputLabelProps={{ shrink: true }}
          sx={fpInputSx}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <TextField
          fullWidth
          type="date"
          label="Fecha de pago"
          value={formulario.fechapago}
          onChange={setCampo('fechapago')}
          InputLabelProps={{ shrink: true }}
          sx={fpInputSx}
        />
      </Grid>

      <Grid item xs={12} md={9}>
        <Box sx={{ bgcolor: '#fff', height: { xs: 420, md: 520 } }}>
          <DataGrid
            rows={periodos}
            columns={columnas}
            getRowId={(r) => `${r.ano}-${r.numero}`}
            rowHeight={46}
            hideFooter={periodos.length <= 25}
            pageSizeOptions={[25]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            slots={{ noRowsOverlay: NoRowsOverlay }}
            sx={{ ...fpDataGridSx, height: '100%', '--DataGrid-overlayHeight': '320px' }}
            disableRowSelectionOnClick
          />
        </Box>
      </Grid>
      <Grid item xs={12} md={3}>
        <Stack spacing={2}>
          <TextField
            fullWidth
            type="date"
            label="Última nómina"
            value={ultimaNomina}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            sx={fpInputSx}
          />
          <TextField
            fullWidth
            label="Año-proceso"
            value={anoProceso}
            onChange={(e) => setAnoProceso(String(e.target.value || '').replace(/\D/g, '').slice(0, 4))}
            inputProps={{ maxLength: 4, inputMode: 'numeric' }}
            InputLabelProps={{ shrink: true }}
            sx={fpInputSx}
          />
          <Button
            onClick={consultarAno}
            disabled={consultando || guardando}
            startIcon={consultando ? <CircularProgress size={16} color="inherit" /> : <SearchOutlinedIcon />}
            sx={btnOutline(FP.color.navy)}
          >
            {consultando ? 'Consultando…' : 'Consultar año'}
          </Button>
        </Stack>
      </Grid>
    </Grid>
  );

  const acciones = (
    <>
      {onClose ? (
        <Button onClick={cerrar} disabled={guardando} sx={btnOutline(FP.color.textSecondary)}>
          Cancelar
        </Button>
      ) : null}
      <Button
        onClick={anadir}
        disabled={guardando || cargando || !puedeUsar || !periodicidad}
        startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
        sx={btnSolid(FP.color.navy, FP.color.navyDeep)}
      >
        {guardando ? 'Guardando…' : 'Añadir'}
      </Button>
    </>
  );

  const confirmacion = (
    <ConfirmarDialog
      abierto={Boolean(eliminando)}
      titulo="Eliminar definición"
      destructivo
      textoConfirmar="Eliminar"
      mensaje={
        eliminando
          ? `¿Confirma eliminar la nómina ${eliminando.numero} del año ${eliminando.ano}? Solo se permite si no hay pagos históricos.`
          : ''
      }
      cargando={borrando}
      onCerrar={() => !borrando && setEliminando(null)}
      onConfirmar={confirmarEliminar}
    />
  );

  if (!puedeUsar) return null;

  if (embedded) {
    return (
      <Box>
        {cuerpo}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>{acciones}</Box>
        {confirmacion}
      </Box>
    );
  }

  return (
    <>
      <Dialog
        open={Boolean(open)}
        onClose={cerrar}
        fullWidth
        maxWidth="xl"
        PaperProps={{
          sx: {
            borderRadius: 3,
            width: '96vw',
            maxWidth: 1400,
            minHeight: { xs: '78vh', md: '82vh' },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Definición de nómina</DialogTitle>
        <DialogContent sx={{ pb: 1 }}>{cuerpo}</DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>{acciones}</DialogActions>
      </Dialog>
      {confirmacion}
    </>
  );
};

export function DefinicionNominaPage() {
  const { controller } = useProductosMaterialContext();
  const { roleDetalles, nombreNegocio } = controller;
  const puedeUsar = useMemo(() => puedeUsarDefinicionNomina(roleDetalles), [roleDetalles]);

  if (!puedeUsar) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface, p: { xs: 2, md: 3 } }}>
        <PageHeader title="Definición de nómina" subtitle="Nómina" />
        <Card sx={{ ...cardShellSx, maxWidth: 560, mx: 'auto', mt: 4 }}>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <LockOutlinedIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 1 }} />
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '1.05rem' }}>
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, mt: 0.5, fontSize: '0.92rem' }}>
              No tienes permisos para definir la nómina.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Definición de nómina"
        subtitle={nombreNegocio ? `${nombreNegocio} · Nómina` : 'Nómina'}
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Card sx={cardShellSx}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <DefinicionNomina embedded open />
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default DefinicionNomina;
